import React, { useEffect, useRef, useState } from 'react';

import { normalizeTestId } from '../../utils';

interface MonthDropdownProps {
  value: number;
  options: { label: string; value: number; disabled?: boolean }[];
  onChange: (month: number) => void;
  id?: string;
  name?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

export const MonthDropdown: React.FC<MonthDropdownProps> = ({
  value,
  options,
  onChange,
  id,
  name,
  ariaLabel,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);

  // Find the index of the current value
  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const selectedLabel = options.find((opt) => opt.value === value)?.label || '';
  const baseTestId = dataTestId ?? 'month-dropdown';
  const comboboxId = id ?? name ?? 'month';
  const labelId = `${comboboxId}-label`;
  const valueId = `${comboboxId}-value`;
  const labelledBy = ariaLabel ? `${labelId} ${valueId}` : valueId;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Scroll to selected item when opening
  useEffect(() => {
    if (isOpen && listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView?.({ block: 'nearest' });
      }
    }
  }, [isOpen, selectedIndex]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView?.({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setHighlightedIndex(selectedIndex);
      }
    }
  };

  const handleSelect = (month: number, isDisabled: boolean) => {
    if (!isDisabled) {
      onChange(month);
      setIsOpen(false);
      setHighlightedIndex(-1);
      comboboxRef.current?.focus();
    }
  };

  const listboxId = `${comboboxId}-listbox`;
  const getOptionId = (optionValue: number) => `${comboboxId}-option-${optionValue}`;
  const highlightedOption = isOpen && highlightedIndex >= 0 ? options[highlightedIndex] : undefined;
  const activeOptionId = highlightedOption ? getOptionId(highlightedOption.value) : undefined;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(selectedIndex);
        } else if (highlightedIndex >= 0) {
          const option = options[highlightedIndex];
          handleSelect(option.value, option.disabled || false);
        }
        break;

      case 'Escape':
        e.preventDefault();
        if (isOpen) {
          e.stopPropagation();
          setIsOpen(false);
          setHighlightedIndex(-1);
          comboboxRef.current?.focus();
        }
        break;

      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(selectedIndex);
        } else {
          setHighlightedIndex((prev) => {
            let next = prev < options.length - 1 ? prev + 1 : prev;
            // Skip disabled options
            while (next < options.length - 1 && options[next]?.disabled) {
              next++;
            }

            return next;
          });
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(selectedIndex);
        } else {
          setHighlightedIndex((prev) => {
            let next = prev > 0 ? prev - 1 : prev;
            // Skip disabled options
            while (next > 0 && options[next]?.disabled) {
              next--;
            }

            return next;
          });
        }
        break;

      case 'Home':
        e.preventDefault();
        if (isOpen) {
          let firstEnabled = 0;
          while (firstEnabled < options.length && options[firstEnabled]?.disabled) {
            firstEnabled++;
          }
          setHighlightedIndex(firstEnabled);
        }
        break;

      case 'End':
        e.preventDefault();
        if (isOpen) {
          let lastEnabled = options.length - 1;
          while (lastEnabled > 0 && options[lastEnabled]?.disabled) {
            lastEnabled--;
          }
          setHighlightedIndex(lastEnabled);
        }
        break;

      default:
        break;
    }
  };

  return (
    <div
      ref={dropdownRef}
      className='month-dropdown'
      data-testid={baseTestId}>
      {ariaLabel && (
        <span
          className='sr-only'
          id={labelId}>
          {ariaLabel}
        </span>
      )}
      <div
        ref={comboboxRef}
        aria-activedescendant={activeOptionId}
        aria-autocomplete='none'
        aria-controls={isOpen ? listboxId : undefined}
        aria-disabled={disabled || undefined}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-labelledby={labelledBy}
        className='month-dropdown__button'
        data-testid={`${baseTestId}-button`}
        id={id}
        role='combobox'
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}>
        <span id={valueId}>{selectedLabel}</span>
        <svg
          aria-hidden='true'
          className='month-dropdown__arrow'
          fill='none'
          focusable='false'
          height='16'
          stroke='currentColor'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          width='16'
          xmlns='http://www.w3.org/2000/svg'>
          <polyline points='6 9 12 15 18 9' />
        </svg>
      </div>

      {/* Hidden input for form submission */}
      {name && (
        <input
          data-testid={`${baseTestId}-hidden-input`}
          name={name}
          type='hidden'
          value={value}
        />
      )}

      {isOpen && (
        <ul
          ref={listRef}
          aria-label={ariaLabel}
          className='month-dropdown__list'
          data-testid={`${baseTestId}-list`}
          id={listboxId}
          role='listbox'
          tabIndex={-1}>
          {options.map((option, index) => (
            <li
              key={option.value}
              aria-disabled={option.disabled}
              aria-selected={option.value === value}
              className={`month-dropdown__option ${option.value === value ? 'month-dropdown__option--selected' : ''} ${
                index === highlightedIndex ? 'month-dropdown__option--highlighted' : ''
              } ${option.disabled ? 'month-dropdown__option--disabled' : ''}`}
              data-testid={`${baseTestId}-option-${normalizeTestId(String(option.value))}`}
              id={getOptionId(option.value)}
              role='option'
              onClick={() => handleSelect(option.value, option.disabled || false)}
              onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}>
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
